/**
 * Protected Route Component
 * 
 * Route guard that checks authentication and permissions
 * Redirects unauthenticated users to Cognito login
 * Redirects unauthorized users to dashboard
 * 
 * Requirements: 1.3, 1.12
 */

import React, { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthProvider';
import { useAuthStore } from '../store/authStore';

interface ProtectedRouteProps {
  children: ReactNode;
  requireAdmin?: boolean;
  requireCoach?: boolean;
}

/**
 * ProtectedRoute Component
 * 
 * Wraps routes that require authentication and/or specific roles
 * 
 * @param children - The component to render if authorized
 * @param requireAdmin - If true, requires admin role
 * @param requireCoach - If true, requires coach or admin role
 */
export function ProtectedRoute({
  children,
  requireAdmin = false,
  requireCoach = false,
}: ProtectedRouteProps) {
  const { isAuthenticated, isAdmin, isCoach, isLoading, user } = useAuth();
  const hasRiders = useAuthStore(state => state.hasRiders);
  const location = useLocation();

  console.log('[ProtectedRoute] Check:', {
    isAuthenticated,
    isAdmin,
    isCoach,
    isLoading,
    requireAdmin,
    requireCoach,
    userGroups: user?.groups,
    path: window.location.pathname
  });

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    console.log('[ProtectedRoute] Not authenticated, redirecting to login');
    // Store the attempted URL for redirect after login
    const currentPath = window.location.pathname + window.location.search;
    sessionStorage.setItem('redirect_after_login', currentPath);
    
    // Redirect to Cognito login
    // Note: In a real implementation, this would trigger redirectToLogin()
    // For now, we'll redirect to a login page that will handle the Cognito redirect
    return <Navigate to="/login" replace />;
  }

  // Check admin permission
  if (requireAdmin && !isAdmin) {
    console.log('[ProtectedRoute] Access denied - requireAdmin=true but isAdmin=false');
    return <Navigate to="/dashboard" replace />;
  }

  // Check coach permission (coach or admin)
  if (requireCoach && !isCoach && !isAdmin) {
    console.log('[ProtectedRoute] Access denied - requireCoach=true, isCoach=', isCoach, 'isAdmin=', isAdmin);
    return <Navigate to="/dashboard" replace />;
  }

  // Redirect non-admin users with no riders to dashboard (they can only see the dashboard guard message)
  // hasRiders === false means dashboard has loaded and confirmed no riders
  // hasRiders === null means dashboard hasn't loaded yet — allow navigation (dashboard will set the flag)
  if (!isAdmin && hasRiders === false && location.pathname !== '/dashboard') {
    console.log('[ProtectedRoute] No riders - redirecting to dashboard');
    return <Navigate to="/dashboard" replace />;
  }

  console.log('[ProtectedRoute] Access granted');
  // User is authenticated and authorized
  return <>{children}</>;
}

/**
 * Login Page Component
 * 
 * Handles the redirect to Cognito login
 * This is a placeholder that will trigger the OAuth flow
 */
export function LoginPage() {
  const { login } = useAuth();

  React.useEffect(() => {
    // Automatically redirect to Cognito login
    login();
  }, [login]);

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Blue Clipboard</h1>
        <p className="text-gray-600 mb-4">Redirecting to login...</p>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
      </div>
    </div>
  );
}
